import type { JwtPayload } from 'jwt-decode';
import { jwtDecode } from 'jwt-decode';
import type { NextResponse } from 'next/server';

import { Cookies } from '@/types';
import { isValueEmptyOrZero } from '@/utils/value';

export interface TokenResponse {
  accessToken: string;
  refreshToken: string;
}

export const AUTH_COOKIE_OPTIONS = {
  httpOnly: true,
  secure: process.env.NODE_ENV === 'production',
  sameSite: 'lax' as const,
  path: '/',
};

export const isTokenExpired = (token: string): boolean => {
  const decoded = jwtDecode<JwtPayload>(token);
  return new Date(decoded.exp! * 1000) < new Date();
};

export const getAuthCookieOptions = (token: string) => {
  const decoded = jwtDecode<JwtPayload>(token);

  return {
    ...AUTH_COOKIE_OPTIONS,
    ...(!isValueEmptyOrZero(decoded.exp)
      ? {
          expires: new Date(decoded.exp! * 1000),
        }
      : {}),
  };
};

export const removeAuthCookies = (response: NextResponse) => {
  response.cookies.delete(Cookies.AccessToken);
  response.cookies.delete(Cookies.RefreshToken);
};

export const refreshTokens = async (token: string, forwardedFor?: string): Promise<TokenResponse> => {
  const res = await fetch(`${process.env.NEXT_PUBLIC_NEXT_API_URL}/api/v1/auth/refresh`, {
    method: 'POST',
    headers: {
      'Content-type': 'application/json',
      ...(forwardedFor ? { 'X-Forwarded-For': forwardedFor } : {}),
    },
    body: JSON.stringify({ token }),
  });
  if (!res.ok) {
    throw new Error('Invalid token');
  }
  return await res.json() as TokenResponse;
};

export const fetchLoggedUserId = async (accessToken: string, forwardedFor?: string): Promise<number> => {
  const res = await fetch(`${process.env.NEXT_PUBLIC_NEXT_API_URL}/api/v1/auth/me`, {
    method: 'GET',
    headers: {
      'Content-type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
      ...(forwardedFor ? { 'X-Forwarded-For': forwardedFor } : {}),
    },
  });
  if (!res.ok) {
    throw new Error('Invalid token');
  }
  const data = await res.json() as { id: number };
  return data.id;
};

export const getRedirectUrl = (pathname: string): string | undefined => {
  switch (pathname) {
    case '/clubs':
      return '/clubs/favorite';
    case '/competitions':
      return '/competitions/my-competitions';
    case '/communication':
      return '/communication/sms';
    case '/dial-pat':
      return '/address-book';
    case '/membership-fees':
      return '/membership-fees/persons';
    case '/personal-settings':
      return '/personal-settings/dashboard';
    case '/player-transfer':
      return '/player-transfer/search';
    case '/qualification':
      return '/qualification/browse';
    case '/referee':
    case '/referee/me':
      return '/referee/me/matches';
    case '/roster':
      return '/roster/my-rosters';
    case '/transfers':
      return '/transfers/home';
    case '/license':
      return '/license/overview';
    default:
      return undefined;
  }
};
